import React, { useEffect, useState } from 'react';
import {
  Cloud,
  CloudRain,
  Sun,
  Wind,
  CloudLightning,
} from 'lucide-react';

const weatherCodeLabel = (code) => {
  if ([0].includes(code)) return 'Clear';
  if ([1,2,3].includes(code)) return 'Cloudy';
  if ([45,48].includes(code)) return 'Fog';
  if ([51,53,55,61,63,65,80,81,82].includes(code)) return 'Rain';
  if ([95,96,99].includes(code)) return 'Storm';
  return 'Forecast';
};

const weatherIcon = (code) => {
  if ([0].includes(code)) return Sun;
  if ([51,53,55,61,63,65,80,81,82].includes(code)) return CloudRain;
  if ([95,96,99].includes(code)) return CloudLightning;
  return Cloud;
};

export default function WeatherForecastWidget() {
  const [weather, setWeather] = useState(null);
  const [locationName, setLocationName] = useState('Operational Area');
  const [error, setError] = useState('');

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const latitude = pos.coords.latitude;
          const longitude = pos.coords.longitude;

          const weatherRes = await fetch(
            `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}` +
            `&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max` +
            `&current=temperature_2m,weather_code,wind_speed_10m` +
            `&forecast_days=7&timezone=auto`
          );

          if (!weatherRes.ok) {
            throw new Error('Weather request failed');
          }

          const weatherData = await weatherRes.json();
          setWeather(weatherData);

          try {
            const geoRes = await fetch(
              `https://geocode.maps.co/reverse?lat=${latitude}&lon=${longitude}`
            );

            if (geoRes.ok) {
              const geo = await geoRes.json();
              const suburb =
                geo?.address?.suburb ||
                geo?.address?.city ||
                geo?.address?.town ||
                geo?.address?.state;

              if (suburb) {
                setLocationName(suburb);
              }
            }
          } catch (e) {
            console.warn('Reverse geocode failed');
          }

        } catch (e) {
          console.error(e);
          setError('Weather unavailable');
        }
      },
      () => {
        setError('Enable location for live forecast');
      }
    );
  }, []);

  if (error) {
    return (
      <div className="rounded-2xl border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-300">
        {error}
      </div>
    );
  }

  if (!weather) {
    return (
      <div className="rounded-2xl border border-[#27272a] bg-[#18181b] p-4 text-sm text-zinc-400">
        Loading 7-day operational forecast...
      </div>
    );
  }

  const daily = weather.daily || {};
  const current = weather.current || {};

  const CurrentIcon = weatherIcon(current.weather_code);

  return (
    <div className="rx-forecast rounded-2xl border border-[#27272a] bg-[#18181b] p-5 shadow-xl">
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="text-xs uppercase tracking-wider text-cyan-400/70">
            Operational Forecast
          </div>

          <h3 className="rx-weather-primary text-xl font-bold text-white">
            {locationName}
          </h3>

          <div className="mt-2 flex items-center gap-3">
            <CurrentIcon className="h-8 w-8 text-cyan-400" />

            <div>
              <div className="rx-weather-primary text-3xl font-bold text-white">
                {Math.round(current.temperature_2m)}°
              </div>

              <div className="rx-weather-muted text-sm text-zinc-400">
                {weatherCodeLabel(current.weather_code)}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-[#09090b] px-3 py-2 text-right">
          <div className="flex items-center gap-2 text-zinc-300">
            <Wind className="h-4 w-4" />
            <span>{Math.round(current.wind_speed_10m)} km/h</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        {(daily.time || []).map((date, i) => {
          const label = new Date(date).toLocaleDateString('en-AU', {
            weekday: 'short'
          });

          const code = daily.weather_code?.[i];
          const max = daily.temperature_2m_max?.[i];
          const min = daily.temperature_2m_min?.[i];
          const rain = daily.precipitation_probability_max?.[i];

          const Icon = weatherIcon(code);

          return (
            <div
              key={date}
              className={`rounded-xl border p-3 ${
                i === 0
                  ? 'border-cyan-500 bg-cyan-500/10'
                  : 'border-[#3f3f46] bg-[#09090b]'
              }`}
            >
              <div className="mb-2 flex items-center justify-between">
                <div className={`rx-weather-${i === 0 ? "today" : "primary"} text-sm font-semibold text-white`}>
                  {i === 0 ? 'Today' : label}
                </div>

                <Icon className="h-4 w-4 text-cyan-400" />
              </div>

              <div className="text-xs text-zinc-400 mb-2">
                {weatherCodeLabel(code)}
              </div>

              <div className="text-lg font-bold text-white">
                {Math.round(max)}°
              </div>

              <div className="text-xs text-zinc-500 mb-2">
                Low {Math.round(min)}°
              </div>

              {rain >= 50 ? (
                <div className="rounded-lg bg-amber-500/20 px-2 py-1 text-[10px] font-semibold text-amber-300">
                  Rain Risk {rain}%
                </div>
              ) : (
                <div className="text-[10px] text-zinc-600">
                  Rain {rain || 0}%
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
